import StaffProfiles, { type StaffPerson } from '../components/StaffProfiles';
import React, { useState, useEffect, useCallback } from 'react';
import EventStatusBadge, { type EventStatus } from '../components/EventStatusBadge';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient, getApiBaseUrl } from '../api/client';
import PaymentQRCode from '../components/PaymentQRCode';
import ImageGallery from '../components/ImageGallery';
import AddressAutocomplete from '../components/AddressAutocomplete';
import MarkdownRenderer from '../components/MarkdownRenderer';
import PasswordInput from '../components/PasswordInput';
import './PublicEventPage.css';
import { translateError } from '../utils/errorMessages';
import { shouldRevealCapacity } from '../utils/capacityVisibility';

/**
 * Get the full URL for an image.
 * If the URL is relative (starts with /), prepend the API base URL.
 * Otherwise, return the URL as-is (assumes it's already absolute).
 */
const getImageUrl = (url: string): string => {
  if (url.startsWith('/')) {
    return `${getApiBaseUrl()}${url}`;
  }
  return url;
};

interface IPublicEvent {
  responsible?: StaffPerson | null;
  _id: string;
  name: string;
  description: string;
  extendedDescription?: string;
  gallery?: string[];
  colorSettings?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
  startTime: string;
  endTime: string;
  price: number;
  /** Per-occurrence status (migration 0056). Absent on legacy payloads. */
  status?: EventStatus;
  capacity?: number;
  remainingSlots?: number | null;
  waitlistCount?: number;
  registeredMemberIds?: string[]; // IDs of user's members already registered
  /**
   * The org's `requiredMemberFields` setting. Public registration creates
   * member records, so the same requirements apply here; this form used to
   * hardcode its own list and ignore the setting entirely.
   */
  requiredMemberFields?: Record<string, boolean>;
}

interface IAttendeeInput {
  _id?: string; // Optional ID if it's an existing member
  name: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  city: string;
  cityDistrict: string;
  nationality: string;
  birthNumber: string;
  street: string;
  zipCode: string;
  buildingNumber: string;
  streetNumber: string;
  email: string;
  phone: string;
  allergies: string;
  mode?: 'existing' | 'new';
}

interface IMyMember {
  _id: string;
  name: string;
}

interface IRegisterResponse {
  success: boolean;
  added: number;
  waitlisted: number;
  token?: string;
  user?: any;
  memberIds?: string[]; // Added to get IDs for QR codes
}

interface ILoginResponse {
  _id: string;
  name: string;
  email: string;
  token: string;
  admin?: boolean;
}

interface PublicEventDetailContentProps {
  /** Public slug of the event to load. */
  slug: string;
}

/**
 * Renders the full event detail + registration UI. Used by both the
 * standalone `/e/:slug` page and the in-page modal opened from the
 * public events list and the carousel.
 */
export const PublicEventDetailContent: React.FC<PublicEventDetailContentProps> = ({ slug }) => {
  const { t } = useTranslation();
  const [event, setEvent] = useState<IPublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ added: number; waitlisted: number; memberIds?: string[] } | null>(null);

  // User form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState(''); // Only for new users

  // Attendees state - default empty attendee object
  const emptyAttendee: IAttendeeInput = {
    name: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    city: '',
    cityDistrict: '',
    nationality: '',
    birthNumber: '',
    street: '',
    zipCode: '',
    buildingNumber: '',
    streetNumber: '',
    email: '',
    phone: '',
    allergies: '',
  };
  const [userInfo, setUserInfo] = useState<any>(() => {
    const stored = localStorage.getItem('userInfo');
    return stored ? JSON.parse(stored) : null;
  });
  const [attendees, setAttendees] = useState<IAttendeeInput[]>(
    () => userInfo ? [] : [{ ...emptyAttendee, mode: 'new' }]
  );
  const [myMembers, setMyMembers] = useState<IMyMember[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Auth mode: 'register' for new users, 'login' for existing users
  const [authMode, setAuthMode] = useState<'register' | 'login'>('register');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError(null);

    try {
      const { data } = await apiClient.post<ILoginResponse>('/api/users/login', {
        email: loginEmail,
        password: loginPassword,
      });

      // Save user info
      localStorage.setItem('userInfo', JSON.stringify(data));
      setUserInfo(data);

      // Fetch user's members (apiClient will use the token from localStorage)
      try {
        const { data: members } = await apiClient.get<IMyMember[]>('/api/my/members');
        setMyMembers(members);
      } catch (err) {
        console.error('Failed to fetch members after login', err);
      }

      // Reset login form
      setLoginEmail('');
      setLoginPassword('');
    } catch (err: any) {
      setLoginError(translateError(err, t, 'publicEvent.loginFailed'));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userInfo');
    setUserInfo(null);
    setMyMembers([]);
    setAttendees([{ ...emptyAttendee }]);
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // apiClient automatically adds auth header if user is logged in
        const { data } = await apiClient.get<IPublicEvent>(`/api/public/events/${slug}`);
        setEvent(data);
        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t, 'publicEvents.loadError'));
        setLoading(false);
      }
    };

    const fetchMyMembers = async () => {
      if (!userInfo) return;
      try {
        const { data } = await apiClient.get<IMyMember[]>('/api/my/members');
        setMyMembers(data);
      } catch (err) {
        console.error('Failed to fetch my members', err);
      }
    };

    if (slug) {
      fetchEvent();
      fetchMyMembers();
    }
  }, [slug, userInfo]);

  const handleAttendeeChange = (index: number, field: keyof IAttendeeInput, value: string) => {
    const newAttendees = [...attendees];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (newAttendees[index] as any)[field] = value;
    setAttendees(newAttendees);
  };

  const addAttendee = () => {
    setAttendees([...attendees, { ...emptyAttendee, mode: 'new' }]);
  };

  const removeAttendee = (index: number) => {
    {
      const newAttendees = [...attendees];
      newAttendees.splice(index, 1);
      setAttendees(newAttendees);
    }
  };

  const handleAddressSelect = useCallback((index: number) => (address: {
    street?: string;
    buildingNumber?: string;
    streetNumber?: string;
    city?: string;
    cityDistrict?: string;
    zipCode?: string;
  }) => {
    setAttendees(prev => {
      const newAttendees = [...prev];
      if (address.street) newAttendees[index].street = address.street;
      if (address.buildingNumber) newAttendees[index].buildingNumber = address.buildingNumber;
      if (address.streetNumber) newAttendees[index].streetNumber = address.streetNumber;
      if (address.city) newAttendees[index].city = address.city;
      if (address.cityDistrict) newAttendees[index].cityDistrict = address.cityDistrict;
      if (address.zipCode) newAttendees[index].zipCode = address.zipCode;
      return newAttendees;
    });
  }, []);

  const getNameSuggestions = (attendeeIndex: number): IMyMember[] => {
    if (!userInfo || myMembers.length === 0) return [];
    const typed = attendees[attendeeIndex].name.trim();
    if (typed.length < 3 || attendees[attendeeIndex]._id) return [];

    const lower = typed.toLowerCase();
    const selectedIds = new Set(
      attendees.filter((_, i) => i !== attendeeIndex).map(a => a._id).filter(Boolean)
    );
    const registeredIds = new Set(event?.registeredMemberIds || []);

    return myMembers
      .filter(m =>
        m.name.toLowerCase().includes(lower) &&
        !registeredIds.has(m._id) &&
        !selectedIds.has(m._id)
      )
      .slice(0, 3);
  };

  const selectSuggestedMember = (index: number, member: IMyMember) => {
    setAttendees(prev => {
      const newAttendees = [...prev];
      newAttendees[index] = {
        ...emptyAttendee,
        _id: member._id,
        name: member.name,
      };
      return newAttendees;
    });
  };

  // Which attendee inputs this organization requires. Driven by the setting the
  // event endpoint now returns, instead of a hardcoded list that demanded
  // fields some orgs don't collect and skipped ones they do.
  const orgRequired = event?.requiredMemberFields ?? {};
  // Name and last name identify the person and stay required regardless.
  const requiredAttendeeField = (fieldName: string, attendee?: IAttendeeInput): boolean => {
    if (fieldName === 'name' || fieldName === 'lastName') return true;
    if (['street', 'city', 'zipCode'].includes(fieldName)) {
      return Boolean(orgRequired.address);
    }
    // Either house number satisfies the server (c.p. or c.o.), so the browser
    // must not block a submit that would validate.
    if (fieldName === 'buildingNumber') {
      return Boolean(orgRequired.address) && !attendee?.streetNumber?.trim();
    }
    return Boolean(orgRequired[fieldName]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate
    const validAttendees = attendees.filter(a => a.name.trim() !== '' || a._id);
    if (validAttendees.length === 0) {
      setError(t('publicEvent.errorNoAttendee'));
      setSubmitting(false);
      return;
    }

    try {
      const existingMemberIds = validAttendees.filter(a => a._id).map(a => a._id);
      const newAttendees = validAttendees.filter(a => !a._id);

      const payload = {
        user: !userInfo ? { email, name, phone, password } : undefined,
        attendees: newAttendees,
        existingMemberIds: existingMemberIds
      };

      // apiClient automatically adds auth header if user is logged in
      const { data } = await apiClient.post<IRegisterResponse>(
        `/api/public/events/${slug}/register`,
        payload
      );

      setSuccess({ 
        added: data.added, 
        waitlisted: data.waitlisted,
        memberIds: data.memberIds 
      });
      
      // If we got a token back (new user auto-login), save it
      if (data.token && data.user) {
        localStorage.setItem('userInfo', JSON.stringify({ ...data.user, token: data.token }));
        // Could redirect or reload, but let's just show success message
      }

    } catch (err: any) {
      if (err.response?.data?.code === 'USER_EXISTS') {
        setAuthMode('login');
        setError(t('publicEvent.userExistsLoginPrompt'));
      } else {
        setError(translateError(err, t, 'publicEvents.registrationError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="public-event-container"><p>{t('common.loading')}</p></div>;
  if (error && !event) return <div className="public-event-container"><p className="error-message">{error}</p></div>;
  if (!event) return <div className="public-event-container"><p>Event not found</p></div>;

  const isWaitlist = event.remainingSlots === 0;
  const isCancelled = event.status === 'cancelled';

  const customStyles = {
    '--event-primary-color': event.colorSettings?.primaryColor || '#2c3e50',
    '--event-secondary-color': event.colorSettings?.secondaryColor || '#3498db',
  } as React.CSSProperties;

  if (success) {
    return (
      <div className="public-event-container" style={customStyles}>
        <div className="success-message">
          <h2>{t('publicEvent.successTitle')}</h2>
          <p>
            {t('publicEvent.successMessage')}
            {success.added > 0 && <span> {t('publicEvent.attendeesConfirmed', { count: success.added })}</span>}
            {success.waitlisted > 0 && <span> {t('publicEvent.attendeesWaitlisted', { count: success.waitlisted })}</span>}
          </p>

          {event.price > 0 && success.added > 0 && success.memberIds && (
            <div className="payment-info-box" style={{ marginTop: '2rem', padding: '1.5rem', background: 'var(--bg-dark)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <h3 style={{ color: 'var(--orange-primary)', marginTop: 0 }}>{t('publicEvent.paymentQR')}</h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{t('publicEvent.paymentQRDesc')}</p>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                {success.memberIds.map(mId => (
                  <div key={mId} style={{ textAlign: 'center' }}>
                    <div style={{ background: 'white', padding: '10px', borderRadius: '8px', display: 'inline-block' }}>
                      <PaymentQRCode eventId={event._id} memberId={mId} size={180} />
                    </div>
                    <p style={{ marginTop: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {event.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '2rem' }}>
            <Link to={userInfo || localStorage.getItem('userInfo') ? "/dashboard" : "/login"} className="btn btn--primary btn--lg" style={{ backgroundColor: 'var(--event-secondary-color)' }}>
              {t('nav.myDashboard')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-event-container" style={customStyles}>
      <style>
        {`
          .public-event-container .btn--primary,
          .public-event-container .register-btn, 
          .public-event-container .add-btn,
          .public-event-container .auth-mode-btn.active {
            background-color: var(--event-secondary-color) !important;
          }
          .public-event-container .event-title {
            color: var(--event-primary-color);
          }
          .public-event-container .section-title {
            border-bottom-color: var(--event-secondary-color);
          }
        `}
      </style>
      <div className="event-header">
        <h1 className="event-title">{event.name}</h1>
        <div className="event-meta">
          {isCancelled && <EventStatusBadge status="cancelled" />}
          <span className="meta-item">📅 {new Date(event.startTime).toLocaleDateString()}</span>
          <span className="meta-item">⏰ {new Date(event.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
          {event.price > 0 && <span className="meta-item">💰 {event.price} CZK</span>}
          {event.capacity && event.remainingSlots !== null && event.remainingSlots !== undefined
            && (isWaitlist || shouldRevealCapacity(event.capacity - event.remainingSlots, event.capacity)) && (
             <span className={`status-badge ${isWaitlist ? 'status-waitlist' : 'status-available'}`}>
               {isWaitlist
                 ? t('publicEvent.waitlistStatus', { count: event.waitlistCount || 0 })
                 : t('publicEvent.slotsStatus', { current: event.capacity - event.remainingSlots, total: event.capacity })}
             </span>
          )}
        </div>
      </div>

      {event.gallery && event.gallery.length > 0 && (
        <div className="event-gallery">
          <ImageGallery
            images={event.gallery}
            getImageUrl={getImageUrl}
            altPrefix={event.name}
          />
        </div>
      )}

      <StaffProfiles people={event.responsible ? [event.responsible] : []} />
      {event.description && event.description.trim() && (
        <div className="event-description">
          <MarkdownRenderer content={event.description} />
        </div>
      )}

      {event.extendedDescription && event.extendedDescription.trim() && (
        <div className="event-extended-description">
          <MarkdownRenderer content={event.extendedDescription} />
        </div>
      )}

      {isCancelled ? (
        // The backend refuses the POST with 409; showing the form anyway would
        // only collect the details to throw them away.
        <div className="registration-section">
          <h2 className="section-title">{t('publicEvent.registration')}</h2>
          <div className="event-cancelled-notice" role="status">
            <strong>{t('publicEvent.cancelledTitle')}</strong>
            <p>{t('publicEvent.cancelledBody')}</p>
          </div>
        </div>
      ) : (
      <div className="registration-section">
        <h2 className="section-title">{t('publicEvent.registration')}</h2>

        <form onSubmit={handleSubmit}>
          {/* User Details Section - Only if not logged in */}
          {!userInfo && (
            <div style={{ marginBottom: '2rem' }}>
              {/* Auth Mode Toggle */}
              <div className="auth-mode-toggle">
                <button
                  type="button"
                  className={`auth-mode-btn ${authMode === 'register' ? 'active' : ''}`}
                  onClick={() => setAuthMode('register')}
                >
                  {t('publicEvent.newUser')}
                </button>
                <button
                  type="button"
                  className={`auth-mode-btn ${authMode === 'login' ? 'active' : ''}`}
                  onClick={() => setAuthMode('login')}
                >
                  {t('publicEvent.existingUser')}
                </button>
              </div>

              {authMode === 'register' ? (
                <>
                  <h3>{t('publicEvent.yourDetails')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('auth.name')}</label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder={t('publicEvent.yourFullName')} />
                    </div>
                    <div className="form-group">
                      <label>{t('common.email')}</label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={t('publicEvent.emailPlaceholder')} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('common.phoneNumber')}</label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+420..." />
                    </div>
                    <div className="form-group">
                      <label>{t('publicEvent.passwordLabel')}</label>
                      <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required minLength={6} placeholder={t('publicEvent.passwordPlaceholder')} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="login-section">
                  <h3>{t('publicEvent.loginTitle')}</h3>
                  <p className="login-description">{t('publicEvent.loginDescription')}</p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('common.email')}</label>
                      <input
                        type="email"
                        value={loginEmail}
                        onChange={e => setLoginEmail(e.target.value)}
                        placeholder={t('publicEvent.emailPlaceholder')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('common.password')}</label>
                      <PasswordInput
                        value={loginPassword}
                        onChange={e => setLoginPassword(e.target.value)}
                        placeholder={t('publicEvent.passwordPlaceholder')}
                      />
                    </div>
                  </div>
                  {loginError && <p className="error-message" style={{ marginBottom: '1rem' }}>{loginError}</p>}
                  <button
                    type="button"
                    className="login-btn"
                    onClick={handleLogin}
                    disabled={loggingIn || !loginEmail || !loginPassword}
                  >
                    {loggingIn ? t('publicEvent.loggingIn') : t('publicEvent.loginButton')}
                  </button>
                </div>
              )}
            </div>
          )}

          {userInfo && (
            <div className="logged-in-info">
              <p>{t('publicEvent.loggedInAs')} <strong>{userInfo.name}</strong> ({userInfo.email})</p>
              <button type="button" className="logout-btn" onClick={handleLogout}>{t('publicEvent.logout')}</button>
            </div>
          )}

          {/* Attendees Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3>{t('publicEvent.attendees')}</h3>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
               {t('publicEvent.attendeesDescription')}
            </p>

            <div className="attendees-list">
              {attendees.map((attendee, index) => (
                <div key={index} className="attendee-card">
                  <div className="attendee-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0 }}>{t('publicEvent.attendee')} {index + 1}</h4>
                    </div>
                    <button type="button" className="remove-btn" onClick={() => removeAttendee(index)} title={t('common.delete')}>×</button>
                  </div>

                  {attendee.mode === 'existing' || attendee._id ? (
                    attendee._id ? (
                      <div className="existing-member-selected">
                        <span>✓</span>
                        <span>{t('publicEvent.existingMemberSelected')}: </span>
                        <span className="member-name">{attendee.name}</span>
                        <button type="button" style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}
                          onClick={() => {
                            const newAttendees = [...attendees];
                            newAttendees[index] = { ...emptyAttendee, mode: 'existing' };
                            setAttendees(newAttendees);
                          }}>{t('common.change')}</button>
                      </div>
                    ) : (
                      <div>
                        <select
                          className="member-select"
                          style={{ width: '100%', marginBottom: '0.5rem' }}
                          value=""
                          onChange={(e) => {
                            const memberId = e.target.value;
                            if (memberId) {
                              const selected = myMembers.find(m => String(m._id) === memberId);
                              if (selected) {
                                const newAttendees = [...attendees];
                                newAttendees[index] = {
                                  ...emptyAttendee,
                                  _id: selected._id,
                                  name: selected.name,
                                  mode: 'existing',
                                };
                                setAttendees(newAttendees);
                              }
                            }
                          }}
                        >
                          <option value="">{t('publicEvent.selectMember')}</option>
                          {myMembers
                            .filter(m => {
                              if (event.registeredMemberIds?.includes(m._id)) return false;
                              const selectedInOtherSlots = attendees
                                .filter((_, i) => i !== index)
                                .some(a => a._id === m._id);
                              return !selectedInOtherSlots;
                            })
                            .map(m => (
                              <option key={m._id} value={m._id}>{m.name}</option>
                            ))}
                        </select>
                      </div>
                    )
                  ) : (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('members.fullName')} *</label>
                          <input
                            type="text"
                            value={attendee.name}
                            onChange={e => {
                              const val = e.target.value;
                              handleAttendeeChange(index, 'name', val);
                              // Auto-sync firstName/lastName from full name
                              const spaceIdx = val.indexOf(' ');
                              if (spaceIdx > 0) {
                                handleAttendeeChange(index, 'firstName', val.substring(0, spaceIdx));
                                handleAttendeeChange(index, 'lastName', val.substring(spaceIdx + 1));
                              } else {
                                handleAttendeeChange(index, 'firstName', '');
                                handleAttendeeChange(index, 'lastName', val);
                              }
                            }}
                            placeholder={t('members.enterName')}
                            required
                          />
                          {(() => {
                            const suggestions = getNameSuggestions(index);
                            if (suggestions.length === 0) return null;
                            return (
                              <div className="member-suggestions" role="status" aria-live="polite">
                                <span className="member-suggestions-label">{t('publicEvent.didYouMean')}</span>
                                {suggestions.map(m => (
                                  <button
                                    key={m._id}
                                    type="button"
                                    className="member-suggestion"
                                    onClick={() => selectSuggestedMember(index, m)}
                                  >
                                    {m.name} — <span>{t('publicEvent.useThisMember')}</span>
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="form-group">
                          <label>{t('publicEvent.dateOfBirth')}{requiredAttendeeField('dateOfBirth') && ' *'}</label>
                          <input
                            type="date"
                            value={attendee.dateOfBirth}
                            onChange={e => handleAttendeeChange(index, 'dateOfBirth', e.target.value)}
                            required={requiredAttendeeField('dateOfBirth')}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('members.firstName')}</label>
                          <input
                            type="text"
                            value={attendee.firstName}
                            onChange={e => handleAttendeeChange(index, 'firstName', e.target.value)}
                            placeholder={t('members.enterFirstName')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('members.lastName')} *</label>
                          <input
                            type="text"
                            value={attendee.lastName}
                            onChange={e => handleAttendeeChange(index, 'lastName', e.target.value)}
                            placeholder={t('members.enterLastName')}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('members.birthNumber')}{requiredAttendeeField('birthNumber') && ' *'}</label>
                          <input
                            type="text"
                            value={attendee.birthNumber}
                            onChange={e => handleAttendeeChange(index, 'birthNumber', e.target.value)}
                            placeholder={t('members.enterBirthNumber')}
                            required={requiredAttendeeField('birthNumber')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('members.nationality')}{requiredAttendeeField('nationality') && ' *'}</label>
                          <input
                            type="text"
                            value={attendee.nationality}
                            onChange={e => handleAttendeeChange(index, 'nationality', e.target.value)}
                            placeholder={t('members.enterNationality')}
                            required={requiredAttendeeField('nationality')}
                          />
                        </div>
                      </div>

                      {/* Address Autocomplete */}
                      <AddressAutocomplete onAddressSelect={handleAddressSelect(index)} />

                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('members.street')}{requiredAttendeeField('street') && ' *'}</label>
                          <input
                            type="text"
                            value={attendee.street}
                            onChange={e => handleAttendeeChange(index, 'street', e.target.value)}
                            placeholder={t('members.enterStreet')}
                            required={requiredAttendeeField('street')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('members.buildingNumber')}{requiredAttendeeField('buildingNumber', attendee) && ' *'}</label>
                          <input
                            type="text"
                            value={attendee.buildingNumber}
                            onChange={e => handleAttendeeChange(index, 'buildingNumber', e.target.value)}
                            placeholder={t('members.enterBuildingNumber')}
                            required={requiredAttendeeField('buildingNumber', attendee)}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('members.streetNumber')}</label>
                          <input
                            type="text"
                            value={attendee.streetNumber}
                            onChange={e => handleAttendeeChange(index, 'streetNumber', e.target.value)}
                            placeholder={t('members.enterStreetNumber')}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('members.city')}{requiredAttendeeField('city') && ' *'}</label>
                          <input
                            type="text"
                            value={attendee.city}
                            onChange={e => handleAttendeeChange(index, 'city', e.target.value)}
                            placeholder={t('members.enterCity')}
                            required={requiredAttendeeField('city')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('members.cityDistrict')}</label>
                          <input
                            type="text"
                            value={attendee.cityDistrict}
                            onChange={e => handleAttendeeChange(index, 'cityDistrict', e.target.value)}
                            placeholder={t('members.enterCityDistrict')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('members.zipCode')}{requiredAttendeeField('zipCode') && ' *'}</label>
                          <input
                            type="text"
                            value={attendee.zipCode}
                            onChange={e => handleAttendeeChange(index, 'zipCode', e.target.value)}
                            placeholder={t('members.enterZipCode')}
                            required={requiredAttendeeField('zipCode')}
                          />
                        </div>
                      </div>
                      {/* Contact + health. The registering account's own email
                          and phone are separate from the attendee's: a parent
                          signing up a child is not the child. */}
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('common.email')}{requiredAttendeeField('email') && ' *'}</label>
                          <input
                            type="email"
                            value={attendee.email}
                            onChange={e => handleAttendeeChange(index, 'email', e.target.value)}
                            placeholder={t('members.enterEmail')}
                            required={requiredAttendeeField('email')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('common.phoneNumber')}{requiredAttendeeField('phone') && ' *'}</label>
                          <input
                            type="tel"
                            value={attendee.phone}
                            onChange={e => handleAttendeeChange(index, 'phone', e.target.value)}
                            placeholder={t('members.enterPhone')}
                            required={requiredAttendeeField('phone')}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>{t('members.allergies')}{requiredAttendeeField('allergies') && ' *'}</label>
                          <textarea
                            rows={2}
                            value={attendee.allergies}
                            onChange={e => handleAttendeeChange(index, 'allergies', e.target.value)}
                            placeholder={t('members.enterAllergies')}
                            required={requiredAttendeeField('allergies')}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {userInfo && myMembers.length > 0 ? (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="add-btn" onClick={() => setAttendees([...attendees, { ...emptyAttendee, mode: 'existing' }])}>+ {t('publicEvent.chooseExisting')}</button>
                <button type="button" className="add-btn" onClick={() => setAttendees([...attendees, { ...emptyAttendee, mode: 'new' }])}>+ {t('publicEvent.addNewAttendee')}</button>
              </div>
            ) : (
              <button type="button" className="add-btn" onClick={addAttendee}>+ {t('publicEvent.addAttendee')}</button>
            )}
          </div>

          {error && <p className="error-message" style={{ marginBottom: '1rem' }}>{error}</p>}

          {!userInfo && authMode === 'login' && (
            <p className="login-hint">{t('publicEvent.loginFirst')}</p>
          )}

          {!isWaitlist && event.remainingSlots != null && attendees.filter(a => a.name.trim() !== '' || a._id).length > event.remainingSlots && (
            <p className="waitlist-warning" style={{ color: 'var(--orange-primary, #e67e22)', fontSize: '0.9rem', margin: '0.5rem 0' }}>
              {t('publicEvent.partialWaitlistWarning', {
                slots: event.remainingSlots,
                attendees: attendees.filter(a => a.name.trim() !== '' || a._id).length
              })}
            </p>
          )}

          <button type="submit" className="register-btn" disabled={submitting || (!userInfo && authMode === 'login')}>
            {submitting ? t('publicEvent.processing') : (isWaitlist ? t('publicEvent.joinWaitlist') : t('publicEvent.registerNow'))}
          </button>
        </form>
      </div>
      )}
    </div>
  );
};

/**
 * Standalone page wrapper for /e/:slug. Pulls the slug from the URL and
 * delegates rendering to `PublicEventDetailContent`.
 */
const PublicEventPage: React.FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  if (!slug) {
    return (
      <div className="public-event-container">
        <p>{t('common.notFound') || 'Event not found'}</p>
      </div>
    );
  }
  return <PublicEventDetailContent slug={slug} />;
};

export default PublicEventPage;
